import { PropTypes } from 'prop-types'
import { useEffect, useRef } from 'react'
import { Form, FormSpy } from 'react-final-form'
import { FormattedMessage } from 'react-intl'
import { useDispatch } from 'react-redux'
import store from 'store2'
import { showLoader } from '../../actions/main-actions'
import { scrollToError } from '../../dom-handlers'
import { trackGA4Ecommerce } from '../../gtm'
import { setGtmDataForSelectedInclusions } from '../../hooks/useResortInclusions'
import { createUrl } from '../../localized-urls'
import { connectCaptcha, getUserData, isStaff, roundPrice } from '../../utils'
import {
  BookingDetailsCardFooter,
  BookingDetailsCardHeader,
  BookingDetailsCardMain,
  BookingDetailsCardSummary,
} from '../account/booking-details/booking-details-card'
import useCheckoutDrData from '../account/booking-details/hooks/useCheckoutDrData'
import { FormCheckbox } from '../forms/final-form-fields'
import { convertCurrency } from '../helpers'
import BookNowPayLater from './BookNowPayLater'
import CheckoutBenefits from './CheckoutBenefits'
import MainGuestForm from './MainGuestForm'
import PaymentSchedule from './PaymentSchedule'
import StaffCheckoutForm from './StaffCheckoutForm'
import { checkoutValidation } from './validation'

CheckoutResortStep3.propTypes = {
  id: PropTypes.string.isRequired,
  dueNowData: PropTypes.object.isRequired,
  onCheckoutFormSubmit: PropTypes.func.isRequired,
  shopLocationSlug: PropTypes.string.isRequired,
  shopTitle: PropTypes.string.isRequired,
  rating: PropTypes.number,
  numberOfReviews: PropTypes.number.isRequired,
  shopPicture: PropTypes.string.isRequired,
  shopLocationTitle: PropTypes.string.isRequired,
  selectedTripData: PropTypes.object.isRequired,
  isFixedPackage: PropTypes.bool.isRequired,
  packagePrice: PropTypes.number.isRequired,
  onVoucherAdd: PropTypes.func.isRequired,
  onCouponRemove: PropTypes.func.isRequired,
  orderEdit: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  slug: PropTypes.string,
  countryCode: PropTypes.string,
}

const initialValues = getUserData()

export default function CheckoutResortStep3({
  id,
  dueNowData,
  orderEdit,
  slug,
  shopTitle,
  rating,
  numberOfReviews,
  shopPicture,
  shopLocationTitle,
  selectedTripData,
  isFixedPackage,
  packagePrice,
  onCheckoutFormSubmit,
  shopLocationSlug,
  onVoucherAdd,
  onCouponRemove,
  countryCode,
}) {
  const dispatch = useDispatch()
  const phoneInstance = useRef()

  const getPhoneInstance = (instance) => {
    phoneInstance.current = instance
  }
  const validate = (values) =>
    checkoutValidation(values, orderEdit, false, phoneInstance.current)

  const { orderDetails, shopData } = useCheckoutDrData(
    dueNowData,
    slug,
    shopTitle,
    rating,
    numberOfReviews,
    shopPicture,
    shopLocationTitle,
    selectedTripData,
    isFixedPackage,
    packagePrice,
    shopLocationSlug,
  )

  const { inclusions, optionsCurrency, ga4EcommerceProduct } = selectedTripData

  const { gtmGA4Products } = setGtmDataForSelectedInclusions(
    inclusions,
    optionsCurrency,
    shopTitle,
    id,
  )

  useEffect(() => {
    dispatch(showLoader(false))
    connectCaptcha('captcha', countryCode)
  }, [])

  useEffect(() => {
    const ga4Extras = dueNowData?.itemsExtra?.map((item) => {
      return {
        item_name: item?.title,
        item_id: (item?.pricing || item?.diveProductId).toString(),
        item_brand: 'PADI',
        item_category: 'PADI Travel',
        item_category2: 'Dive Resort',
        item_category3: 'Extras',
        item_list_name: 'PADI Travel Dive Resorts - Add Extras',
        price: Number(
          roundPrice(convertCurrency(item?.price, item?.currency, 'USD')),
        ),
        quantity: item?.quantity,
      }
    })
    const items = [ga4EcommerceProduct, ...gtmGA4Products, ...ga4Extras]
    trackGA4Ecommerce(
      'begin_checkout',
      {
        currency: 'USD',
        value: Number(
          convertCurrency(
            dueNowData?.taxFreeAmount,
            dueNowData?.currency,
            'USD',
          ),
        ),
        items,
      },
      'PADI Travel - Dive Resorts',
    )
    store.session.set('ecommerce', items)
  }, [dueNowData])

  if(!selectedTripData || !orderDetails) {
    return null
  }

  return (
    <div className='step3'>
      <div className='complete-wrap'>
        <h2 className='step-title'>
          <span className='number'>3</span>
          <FormattedMessage
            id='enter_your_information'
            defaultMessage='Enter your information and book'
          />
        </h2>

        <div className='order-total'>
          <div className='pan-white'>
            <BookingDetailsCardHeader order={shopData} />
            <div className='pad'>
              {selectedTripData?.requirements && (
                <div className='resort-requirements step-3'>
                  <p>
                    <FormattedMessage defaultMessage='Requirements for divers:' />
                  </p>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: selectedTripData?.requirements,
                    }}
                  />
                </div>
              )}
              <BookingDetailsCardSummary order={orderDetails} />
              <BookingDetailsCardMain
                order={shopData}
                onCouponRemove={onCouponRemove}
                guestsInRoom={orderDetails.guestsInRoom}
              />
              <BookingDetailsCardFooter
                order={shopData}
                showCouponForm
                onVoucherAdd={onVoucherAdd}
              />

              <PaymentSchedule
                dueNowData={dueNowData}
                freeCancellationDays={selectedTripData.freeCancellationDays}
                shopKind={shopData.shopKind}
              />
            </div>
          </div>
        </div>
        <Form
          onSubmit={onCheckoutFormSubmit}
          initialValues={initialValues}
          validate={validate}
          render={({ handleSubmit, values }) => (
            <form onSubmit={handleSubmit}>
              <FormSpy
                subscription={{ submitFailed: true }}
                onChange={scrollToError}
              />
              {!orderEdit && (
                <MainGuestForm
                  getPhoneInstance={getPhoneInstance}
                  handleSubmit={handleSubmit}
                  shopKind={shopData.shopKind}
                  countryCode={countryCode}
                />
              )}
              {isStaff() && <StaffCheckoutForm values={values} />}
              <BookNowPayLater
                shopKind={shopData.shopKind}
              />
              <div className='fields'>
                <div className='mbm'>
                  <div id='captcha' />
                </div>
                <div className='btn-wrap'>
                  <button type='submit' className='btn-red'>
                    {orderEdit ? (
                      <FormattedMessage
                        id='create_option'
                        defaultMessage='Update order'
                      />
                    ) : (
                      <FormattedMessage
                        id='request_to_book'
                        defaultMessage='Request to book'
                      />
                    )}
                  </button>
                  <CheckoutBenefits
                    freeCancellationDays={selectedTripData.freeCancellationDays}
                  />
                </div>
              </div>
            </form>
          )}
        />
      </div>
    </div>
  )
}
